import { useCallback, useState } from "react";
import {
	Divider,
	IconButton,
	List,
	type ListItemProps,
	Searchbar,
	type SearchbarProps,
} from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";

export interface SearchViewModalProps extends SearchbarProps {
	items: ListItemProps[];
	onItemClick?: (item: ListItemProps) => void;
	onClear?: () => void;
	onBack?: () => void;
	rightIcon?: string;
	showCloseIcon?: boolean;
}

const SearchViewModal = ({
	items = [],
	onItemClick,
	onChangeText,
	onClear,
	onBack,
	showCloseIcon = true,
	rightIcon = "close",
	...props
}: SearchViewModalProps) => {
	const theme = useAppTheme();
	const [_searchQuery, setSearchQuery] = useState(props.defaultValue || "");
	const [isExpanded, setIsExpanded] = useState(items.length > 0);

	const handleChange = useCallback(
		(text: string) => {
			setSearchQuery(text);
			onChangeText?.(text);
			setIsExpanded(text.length > 0);
		},
		[onChangeText],
	);

	const handleClear = useCallback(() => {
		setSearchQuery("");
		onChangeText?.("");
		onClear?.();
		setIsExpanded(false);
	}, [onChangeText, onClear]);

	return (
		<Box
			width="100%"
			backgroundColor={theme.colors.surfaceVariant}
			borderRadius={28}
			position="relative"
		>
			<Searchbar
				theme={theme}
				placeholder="Search"
				onChangeText={handleChange}
				icon={props.icon || "arrow-left"}
				onIconPress={() => onBack?.()}
				right={(props) =>
					showCloseIcon && (
						<IconButton
							onPress={() => handleClear()}
							{...props}
							icon={rightIcon}
						/>
					)
				}
				placeholderTextColor={theme.colors.onSurfaceVariant}
				inputStyle={{
					color: theme.colors.onSurface,
				}}
				style={{
					backgroundColor: theme.colors.surfaceVariant,
				}}
				{...props}
			/>
			{isExpanded && items.length > 0 && <Divider />}

			{isExpanded &&
				items.length > 0 &&
				items.map((item, index) => (
					<List.Item
						key={item.id}
						{...item}
						style={[
							{
								paddingHorizontal: 16,
								paddingVertical: 8,
								flex: 1,
								backgroundColor: theme.colors.surfaceVariant,
								borderBottomLeftRadius: index === items.length - 1 ? 28 : 0,
								borderBottomRightRadius: index === items.length - 1 ? 28 : 0,
							},
						]}
						onPress={() => onItemClick?.(item)}
					/>
				))}
		</Box>
	);
};

export default SearchViewModal;
